import { useEffect, useRef } from 'react';
import { Image } from 'expo-image';
import { router } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import {
  Animated,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts } from '@/constants/theme';
import logoSource from '../assets/images/mcqgen-logo.png';

export default function LandingScreen() {
  const insets = useSafeAreaInsets();
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // Start progress animation
    Animated.timing(progress, {
      toValue: 1,
      duration: 2500, // 2.5 seconds
      useNativeDriver: false,
    }).start(({ finished }) => {
      if (finished) {
        router.replace('/sign-in');
      }
    });
  }, [progress]);

  const progressBarWidth = progress.interpolate({
    inputRange: [0, 1],
    outputRange: ['0%', '100%'],
  });

  return (
    <SafeAreaView edges={['top', 'bottom']} style={styles.safeArea}>
      <StatusBar style="dark" />
      <View
        style={[
          styles.screen,
          {
            paddingTop: Math.max(insets.top, 16) + 12,
            paddingBottom: Math.max(insets.bottom, 20) + 8,
          },
        ]}>
        <View style={[styles.glow, styles.glowTop]} />
        <View style={[styles.glow, styles.glowBottom]} />

        <View style={styles.hero}>
          <Image contentFit="contain" source={logoSource} style={styles.logo} />

          <View style={styles.copyBlock}>
            <Text style={styles.title}>Build polished MCQs in seconds.</Text>
            <Text style={styles.subtitle}>
              Designing the future of academic assessments with speed and precision.
            </Text>
          </View>
        </View>

        <View style={styles.footer}>
          <View style={styles.loaderContainer}>
            <View style={styles.progressBarBg}>
              <Animated.View style={[styles.progressBarFill, { width: progressBarWidth }]} />
            </View>
            <Text style={styles.loadingText}>Initializing workspace...</Text>
          </View>
          <Text style={styles.footerHint}>School-first access • Better exams.</Text>
        </View>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  screen: {
    flex: 1,
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 24,
    overflow: 'hidden',
  },
  glow: {
    position: 'absolute',
    borderRadius: 999,
    opacity: 0.5,
  },
  glowTop: {
    width: 220,
    height: 220,
    top: -72,
    right: -78,
    backgroundColor: '#DCEBDF',
  },
  glowBottom: {
    width: 260,
    height: 260,
    bottom: -110,
    left: -70,
    backgroundColor: '#EFF6F0',
  },
  hero: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 26,
  },
  logo: {
    width: 200,
    height: 200,
  },
  copyBlock: {
    alignItems: 'center',
    gap: 12,
  },
  title: {
    maxWidth: 320,
    color: '#173924',
    fontSize: 28,
    fontFamily: Fonts.rounded,
    fontWeight: '700',
    lineHeight: 34,
    textAlign: 'center',
    letterSpacing: -0.6,
  },
  subtitle: {
    maxWidth: 300,
    color: '#5D7162',
    fontSize: 14,
    fontFamily: Fonts.serif,
    lineHeight: 20,
    textAlign: 'center',
    opacity: 0.8,
  },
  footer: {
    gap: 20,
    alignItems: 'center',
    marginBottom: 20,
  },
  loaderContainer: {
    width: '100%',
    alignItems: 'center',
    gap: 10,
  },
  progressBarBg: {
    width: '100%',
    height: 6,
    backgroundColor: '#F0F4F0',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#1B5E20',
  },
  loadingText: {
    color: '#1B5E20',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
    textTransform: 'uppercase',
    opacity: 0.6,
  },
  footerHint: {
    color: '#BDBDBD',
    textAlign: 'center',
    fontSize: 12,
    letterSpacing: 0.2,
  },
});
